/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, CheckCheck, Menu, X,
  Camera, ChevronRight, ChevronLeft, ChevronDown,
  Loader2, Send, Trash2, Plus,
  Eye, Settings, ListChecks, Package, Ruler, Cpu,
  Image as ImageIcon, Images,
  Mail, Smartphone, Inbox, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const track = (event) => {
  if (typeof window !== "undefined" && typeof window.plausible === "function") {
    try { window.plausible(event); } catch { /* no-op */ }
  }
};

export default function Landing() {
  useEffect(() => {
    import("@/lib/analytics").then(({ captureSignupSource }) => captureSignupSource()).catch(() => {});
    const badge = document.getElementById("emergent-badge");
    const prev = badge ? badge.style.display : null;
    if (badge) badge.style.display = "none";
    return () => { if (badge) badge.style.display = prev || ""; };
  }, []);

  return (
    <div className="bg-white text-slate-900 antialiased selection:bg-emerald-200 selection:text-emerald-900">
      <Nav />
      <Hero />
      <ProblemSolution />
      <UnitWorkspace />
      <TemplatesSection />
      <TeamSection />
      <Pricing />
      <Faq />
      <ContactForm />
      <Footer />
      <FloatingCta />
    </div>
  );
}

const CONTAINER = "mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12";

/* -------------------------------------------------------------------------- */
/*  NAV                                                                       */
/* -------------------------------------------------------------------------- */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { href: "#ratkaisu", label: "Ratkaisu" },
    { href: "#nain-toimii", label: "Näin Veraso toimii" },
    { href: "#hinnoittelu", label: "Hinnoittelu" },
    { href: "#ukk", label: "UKK" },
    { href: "#ota-yhteytta", label: "Ota yhteyttä" },
  ];
  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70" : "bg-transparent"}`} data-testid="landing-nav">
      <div className={`${CONTAINER} h-16 flex items-center justify-between`}>
        <a href="#top" className="inline-flex items-center gap-2" data-testid="landing-nav-logo">
          <span className="font-display font-semibold text-lg tracking-tight"><span className="text-blue-600">Veraso</span> <span className="text-emerald-600">Reports</span></span>
        </a>
        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-600">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-slate-900 transition-colors">{l.label}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a href="mailto:info@veraso.fi" className="hidden lg:inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-emerald-600 transition-colors" data-testid="landing-nav-email">
            <Mail className="h-4 w-4" /> info@veraso.fi
          </a>
          <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-3 py-1.5" data-testid="landing-nav-login">Kirjaudu</Link>
          <Link to="/register" onClick={() => track("nav_register")} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 shadow-sm" data-testid="landing-nav-register">
            Aloita ilmaiseksi <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {/* Mobile: näkyvä "Kirjaudu" -linkki hamburgerin vieressä jotta
            paluukäyttäjien (asentajien) ei tarvitse avata valikkoa. */}
        <div className="lg:hidden flex items-center gap-1">
          <Link
            to="/login"
            className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-100"
            data-testid="landing-nav-login-mobile"
          >
            Kirjaudu
          </Link>
          <button type="button" className="p-2 rounded-lg hover:bg-slate-100" onClick={() => setOpen((v) => !v)} aria-label="Valikko" data-testid="landing-nav-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200">
          <div className={`${CONTAINER} py-4 space-y-2`}>
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block py-2 text-slate-700 font-medium">{l.label}</a>
            ))}
            <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
              <a href="mailto:info@veraso.fi" onClick={() => setOpen(false)} className="inline-flex items-center gap-2 text-slate-600 py-2 text-sm font-medium" data-testid="landing-nav-email-mobile">
                <Mail className="h-4 w-4" /> info@veraso.fi
              </a>
              <Link to="/login" className="text-slate-700 py-2 font-semibold">Kirjaudu</Link>
              <Link to="/register" onClick={() => { setOpen(false); track("nav_register"); }} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 text-white font-semibold px-4 py-2.5">
                Aloita ilmaiseksi <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/*  HERO                                                                       */
/* -------------------------------------------------------------------------- */
const INDUSTRIES = [
  "Huoltoyritykset", "Sähköasennukset", "Työmaat ja rakennusvalvonta",
  "Tarkastukset", "Kiinteistöhuolto", "LVI ja putkityöt", "Field service ja korjausluovutukset",
];
function Hero() {
  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      <style>{`
        @keyframes veraso-float-a { 0%,100%{ transform: translateY(0) rotate(-3deg);} 50%{ transform: translateY(-8px) rotate(-3deg);} }
        @keyframes veraso-float-b { 0%,100%{ transform: translateY(0) rotate(6deg);} 50%{ transform: translateY(-6px) rotate(6deg);} }
        .veraso-float-a { animation: veraso-float-a 6s ease-in-out infinite; }
        .veraso-float-b { animation: veraso-float-b 7s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .veraso-float-a, .veraso-float-b { animation: none; }
        }
      `}</style>
      <div aria-hidden className="absolute inset-x-0 top-0 h-[700px] -z-10 pointer-events-none">
        <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-[radial-gradient(closest-side,rgba(16,185,129,0.30),rgba(16,185,129,0.10)_45%,transparent_75%)] blur-3xl" />
      </div>
      <div className={CONTAINER}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900" data-testid="hero-headline">
              Raportteja ei tarvitse rakentaa — <span className="text-emerald-600">ne syntyvät työn mukana.</span>
            </h1>
            <p className="mt-6 text-xl sm:text-2xl text-slate-600 max-w-3xl leading-relaxed" data-testid="hero-subtext">
              Luo työ muutamassa minuutissa. Veraso muodostaa valmiin rungon, jota asentaja täydentää työn aikana — ilman monimutkaisten raporttipohjien rakentamista.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/register" onClick={() => track("hero_register")} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-7 py-4 text-base shadow-lg shadow-emerald-600/20 transition-all hover:-translate-y-0.5" data-testid="hero-cta-primary">
                Aloita ilmaiseksi <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500" data-testid="hero-trial-note">
              <span className="font-semibold text-slate-700">Aloita ilmainen 30 päivän kokeilu</span>
              <span className="text-slate-300">·</span>
              <span>Ei vaadi luottokorttia</span>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <HeroPhoneStack />
          </div>
        </div>

        {/* Industries strip */}
        <div className="mt-16 md:mt-20 border-y border-slate-200 py-10" data-testid="hero-industries">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Kaikille kenttätyön aloille</div>
              <div className="font-display text-3xl font-semibold text-slate-900 leading-tight">Tehty kenttätyölle.</div>
              <p className="text-lg text-slate-600 mt-4 leading-relaxed">
                Nopeasta huoltokäynnistä monisivuisiin tarkastusprojekteihin. Sama työkalu skaalautuu yhdestä yksiköstä useamman päivän kokonaisuuteen.
              </p>
            </div>
            <div className="md:col-span-8">
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((it) => (
                  <span key={it} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-slate-50 border border-slate-200 text-base font-medium text-slate-700">
                    {it}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  HERO PHONE STACK — two overlapping mockups                                 */
/* -------------------------------------------------------------------------- */
function HeroPhoneStack() {
  return (
    <div className="relative h-[580px] w-full" data-testid="hero-visual">
      {/* Soft emerald halo behind phones */}
      <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-16 right-6 w-[420px] h-[420px] bg-[radial-gradient(closest-side,rgba(16,185,129,0.20),transparent_70%)] blur-2xl" />
      </div>

      {/* Back phone — Unit detail (rotated right) */}
      <div className="absolute right-0 top-8 veraso-float-b will-change-transform">
        <HeroPhoneShell>
          <HeroUnitDetail />
        </HeroPhoneShell>
      </div>

      {/* Front phone — Unit list (rotated left) */}
      <div className="absolute left-0 top-16 veraso-float-a will-change-transform z-10">
        <HeroPhoneShell>
          <HeroUnitList />
        </HeroPhoneShell>
      </div>
    </div>
  );
}

function HeroPhoneShell({ children }) {
  return (
    <div className="w-[270px] rounded-[40px] bg-slate-900 p-1.5 shadow-[0_35px_80px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-900/5">
      <div className="rounded-[34px] bg-white overflow-hidden">
        <div className="h-5 bg-slate-900 rounded-b-2xl mx-auto w-24 relative -top-px" />
        <div className="px-4 pt-3 pb-5 min-h-[500px]">{children}</div>
      </div>
    </div>
  );
}

function HeroUnitList() {
  const rows = ["Asunto 1", "Asunto 2", "Asunto 3"];
  return (
    <div className="space-y-3.5">
      <div className="flex items-start justify-between">
        <div className="font-display font-bold text-slate-900 text-lg leading-tight">LVI-remontti</div>
        <div className="text-[10px] text-slate-400 pt-1">Ei tallennuksia</div>
      </div>
      <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium">
        <Eye className="h-3 w-3" /> Esikatselu
      </div>
      <div className="rounded-2xl bg-white border border-slate-100 divide-y divide-slate-100 overflow-hidden shadow-sm">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 px-3.5 py-3">
            <div className="h-4 w-4 rounded-full border-2 border-slate-300 flex-shrink-0" />
            <span className="text-sm font-bold text-slate-900 flex-1">{r}</span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium">Avoin</span>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
          </div>
        ))}
      </div>
      <button className="w-full rounded-2xl bg-emerald-500 text-white px-4 py-3.5 flex items-center justify-center gap-2 shadow-md shadow-emerald-500/20">
        <CheckCheck className="h-4 w-4" />
        <span className="text-[13px] font-bold">Merkitse koko kohde valmiiksi</span>
        <span className="text-[10px] opacity-80">· 3 yksikköä avoinna</span>
      </button>
      <button className="w-full rounded-2xl border border-emerald-200 bg-emerald-50/40 text-emerald-800 px-4 py-3 flex items-center justify-center gap-2">
        <Settings className="h-3.5 w-3.5" />
        <span className="text-[12px] font-semibold">Hallinnoi yksiköitä</span>
      </button>
      <div className="pt-1 text-center text-[10px] text-slate-400">
        Tehty <span className="text-emerald-600 font-semibold">Verasolla</span>
      </div>
    </div>
  );
}

function HeroUnitDetail() {
  const rows = [
    { icon: ImageIcon, label: "Kuvat", sub: "Ei kuvia" },
    { icon: ListChecks, label: "Tehdyt toimenpiteet", sub: "Ei merkintöjä" },
    { icon: Package, label: "Tarvikkeet", sub: "Ei tarvikkeita" },
    { icon: Ruler, label: "Mittaustulokset", sub: "Ei mittauksia" },
    { icon: Cpu, label: "Laitetiedot", sub: "Ei laitteita" },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold">
          <ChevronLeft className="h-3 w-3" /> Yksiköt
        </div>
        <div className="text-center">
          <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-600">Yksikkö</div>
          <div className="font-display font-bold text-slate-900 text-sm leading-none mt-0.5">Asunto 1</div>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium">Avoin</span>
      </div>
      <div className="text-right text-[9px] text-slate-400">Ei tallennuksia</div>
      <div className="rounded-2xl bg-white border border-slate-100 divide-y divide-slate-100 overflow-hidden shadow-sm">
        {rows.map((r, i) => {
          const Icon = r.icon;
          return (
            <div key={i} className="flex items-center gap-2.5 px-3 py-2.5">
              <div className="h-7 w-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-bold text-slate-900 leading-tight">{r.label}</div>
                <div className="text-[10px] text-slate-500">{r.sub}</div>
              </div>
              <ChevronRight className="h-3 w-3 text-slate-300" />
            </div>
          );
        })}
      </div>
      <div className="rounded-2xl bg-emerald-50/50 border border-emerald-100 p-3">
        <div className="text-[12px] font-bold text-slate-900">Aloita kuvien lisääminen</div>
        <div className="text-[10px] text-slate-500 mb-2.5">Dokumentoi työ ja lisää kuvat</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-white border border-emerald-100 py-2.5 flex flex-col items-center gap-1">
            <div className="h-7 w-7 rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <Images className="h-3.5 w-3.5" />
            </div>
            <span className="text-[10px] font-semibold text-slate-800">Valitse galleriasta</span>
          </div>
          <div className="rounded-xl bg-white border border-emerald-100 py-2.5 flex flex-col items-center gap-1">
            <div className="h-7 w-7 rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <Camera className="h-3.5 w-3.5" />
            </div>
            <span className="text-[10px] font-semibold text-slate-800">Ota kuva</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PROBLEM + SOLUTION (side-by-side)                                          */
/* -------------------------------------------------------------------------- */
function ProblemSolution() {
  const problems = [
    "Kuvat puuttuvat tai eivät päädy raportille.",
    "Havaintoja unohtuu.",
    "Tarvikkeita jää laskuttamatta.",
    "Asiakas saa liian vähän dokumentaatiota.",
    "Jatkotöitä jää tarjoamatta.",
    "Asiakkaat vaativat aiempaa parempaa dokumentointia.",
  ];
  const solutions = [
    "Raportti luodaan valmiiksi muutamassa minuutissa.",
    "Kuvat tallentuvat suoraan oikealle yksikölle.",
    "Havainnot muuttuvat automaattisesti jatkotoimenpiteiksi.",
    "Tarvikkeet eivät jää irrallisiksi muistiinpanoiksi.",
    "Asiakkaalle muodostuu selkeä raportti yrityksesi omalla ilmeellä.",
  ];
  return (
    <section id="ratkaisu" className="py-20 md:py-28">
      <div className={CONTAINER}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          <div className="rounded-3xl bg-slate-50 border border-slate-200 p-10 md:p-14" data-testid="problem-panel">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-5">Ongelma</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-slate-900">
              Työ tehdään hyvin<br />— raportti ei aina kerro sitä.
            </h2>
            <p className="mt-7 text-lg text-slate-600 leading-relaxed">
              Työpäivän aikana syntyy paljon tärkeää tietoa. Kuvat jäävät puhelimeen, havaintoja unohtuu ja tarvikkeita jää kirjaamatta. Kun raportti tehdään jälkikäteen, kaikki tärkeät yksityiskohdat eivät enää ole muistissa.
            </p>
            <ul className="mt-8 space-y-4">
              {problems.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-800">
                  <div className="h-6 w-6 rounded-md bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="h-3.5 w-3.5 text-slate-400" />
                  </div>
                  <span className="leading-relaxed text-base md:text-lg">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-emerald-50/40 border border-emerald-200 p-10 md:p-14" data-testid="solution-panel">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-5">Ratkaisu</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-slate-900">
              Näin Veraso ratkaisee sen.
            </h2>
            <p className="mt-7 text-lg text-slate-700 leading-relaxed">
              Työ dokumentoidaan suoraan puhelimella työn aikana. Kuvat, havainnot, mittaukset ja tehdyt toimenpiteet tallentuvat oikeaan paikkaan heti, kun ne syntyvät.
            </p>
            <ul className="mt-8 space-y-4">
              {solutions.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-800">
                  <div className="h-6 w-6 rounded-md bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="leading-relaxed text-base md:text-lg">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  UNIT WORKSPACE — 3 phones + numbered steps                                 */
/* -------------------------------------------------------------------------- */
function UnitWorkspace() {
  return (
    <section id="nain-toimii" className="py-20 md:py-28 bg-slate-50/60">
      <div className={CONTAINER}>
        <div className="max-w-3xl mb-14">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-slate-900">
            Jokaiselle yksikölle tai työvaiheelle<br />
            <span className="text-slate-400">oma selkeä työtila.</span>
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed text-xl max-w-2xl">
            Raportointijärjestelmät ovat usein pitkiä lomakkeita. Veraso on suunniteltu helpottamaan kenttätyöntekijän arkea, ja jokaisella yksiköllä tai työvaiheella on oma selkeä työtila — jolloin dokumentointi helpottuu.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8 mt-12">
          <PhoneCard number="01" caption="Yksikkölista"><PhoneUnitList /></PhoneCard>
          <PhoneCard number="02" caption="Yksikkö aukaistu"><PhoneUnitOpen /></PhoneCard>
          <PhoneCard number="03" caption="Hallinnoi yksiköitä"><PhoneManageUnits /></PhoneCard>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12">
          {[
            { n: "01", h: "Yksikkö, laite, tarkastuspiste tai työvaihe", body: "Jokaisella oma mutkaton osio, jossa kuvat ja lisätiedot kirjataan helposti." },
            { n: "02", h: "Tarvikkeet yhdellä napautuksella", body: "Vakiotarvikkeet ovat valmiina pikanapeissa. Lisää havainnot ja jatkotoimenpiteet — kaikki tallentuu suoraan oikeaan yksikköön ja kulkeutuu laskutukseen asti." },
            { n: "03", h: "Hallinnoi yksiköitä lennossa", body: "Suunnitelmat muuttuvat työmaalla. Lisää, poista tai täytä yksiköitä matkan varrella — yksittäin tai monta kerralla." },
          ].map((c, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-7" data-testid={`unit-step-${c.n}`}>
              <div className="text-3xl font-display font-bold text-emerald-600 tabular-nums leading-none">{c.n}</div>
              <div className="mt-3 font-semibold text-slate-900 text-lg">{c.h}</div>
              <div className="mt-2 text-base text-slate-600 leading-relaxed">{c.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PhoneCard({ number, caption, children }) {
  return (
    <div className="relative flex flex-col items-center">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
        <div className="h-8 w-8 rounded-full bg-emerald-500 text-white font-bold text-sm flex items-center justify-center shadow-lg ring-4 ring-slate-50">
          {parseInt(number, 10)}
        </div>
      </div>
      <div className="relative">
        <div className="mx-auto w-[260px] rounded-[36px] bg-slate-900 p-1.5 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.35)]">
          <div className="rounded-[30px] bg-white overflow-hidden">
            <div className="h-4 bg-slate-900 rounded-b-xl mx-auto w-20 relative -top-px" />
            <div className="p-4 pt-2 pb-6 min-h-[440px]">{children}</div>
          </div>
        </div>
      </div>
      <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs text-slate-500 font-medium">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        {caption}
      </div>
    </div>
  );
}

function PhoneUnitList() {
  const rows = ["Asunto 1", "Asunto 2", "Asunto 3", "Asunto 4", "Asunto 5"];
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold text-slate-500">Sähköasennusprojekti</div>
      <div className="flex gap-2 text-[10px]">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600">Esikatselu</span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Luo yhteenveto</span>
      </div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 py-2 border-b border-slate-100">
            <div className="h-4 w-4 rounded-full border-2 border-slate-300" />
            <span className="text-xs font-semibold text-slate-800 flex-1">{r}</span>
            <span className="text-[10px] text-slate-400">Avoin</span>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-emerald-500 text-white px-3 py-3 text-center">
        <div className="flex items-center justify-center gap-1.5 text-xs font-bold">
          <Check className="h-3.5 w-3.5" /> Merkitse koko kohde valmiiksi
        </div>
        <div className="text-[10px] opacity-80 mt-0.5">5 yksikköä avoinna</div>
      </div>
      <button className="w-full py-2.5 rounded-xl bg-slate-100 text-xs font-semibold text-slate-700">Hallinnoi yksiköitä</button>
    </div>
  );
}

function PhoneUnitOpen() {
  const items = [
    { icon: "📷", label: "Kuvat", sub: "Ei kuvia" },
    { icon: "☑", label: "Tehdyt toimenpiteet", sub: "Ei merkintöjä" },
    { icon: "⚠", label: "Havainnot / Jatkotoimenpiteet", sub: "Ei havaintoja" },
    { icon: "📦", label: "Tarvikkeet", sub: "Ei tarvikkeita" },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 text-[10px] text-slate-400"><ChevronLeft className="h-3 w-3" /> Yksiköt</div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Yksikkö</div>
      <div className="font-display font-bold text-slate-900">Asunto 1</div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 rounded-xl bg-slate-50 px-2.5 py-2">
            <div className="h-7 w-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-xs">{it.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-slate-800 leading-tight">{it.label}</div>
              <div className="text-[9px] text-slate-500">{it.sub}</div>
            </div>
            <ChevronRight className="h-3 w-3 text-slate-300" />
          </div>
        ))}
      </div>
      <div className="pt-1">
        <div className="text-[11px] font-semibold text-slate-800">Aloita kuvien lisääminen</div>
        <div className="text-[9px] text-slate-500 mb-2">Dokumentoi työ ja lisää kuvat</div>
        <div className="grid grid-cols-2 gap-1.5">
          <button className="rounded-xl bg-emerald-50 border border-emerald-200 py-2.5 text-emerald-800 text-[10px] font-bold">📁 Valitse galleriasta</button>
          <button className="rounded-xl bg-emerald-500 text-white py-2.5 text-[10px] font-bold inline-flex items-center justify-center gap-1"><Camera className="h-3 w-3" /> Ota kuva</button>
        </div>
      </div>
    </div>
  );
}

function PhoneManageUnits() {
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold text-slate-500">Sähköasennusprojekti</div>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm text-slate-900">Hallinnoi yksiköitä</div>
        <X className="h-3.5 w-3.5 text-slate-400" />
      </div>
      <div>
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Lisää yksittäinen</div>
        <div className="flex gap-1.5">
          <div className="flex-1 rounded-lg bg-white border border-slate-200 px-2 py-1.5 text-[10px] text-slate-400">esim. Asunto 6</div>
          <button className="rounded-lg bg-emerald-500 text-white text-[10px] font-bold px-3">Lisää</button>
        </div>
      </div>
      <div>
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Lisää monta kerralla</div>
        <div className="rounded-lg bg-white border border-slate-200 p-2 text-[10px] text-slate-500 space-y-0.5">
          <div>Asunto 6</div>
          <div>Asunto 7</div>
          <div>Asunto 8</div>
        </div>
        <button className="mt-2 w-full rounded-full bg-emerald-500 text-white text-[10px] font-bold py-2 flex items-center justify-center gap-1"><Plus className="h-3 w-3" /> Lisää kaikki</button>
      </div>
      <div>
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Nykyiset yksiköt (5)</div>
        <div className="space-y-1">
          {["Asunto 1", "Asunto 2", "Asunto 3", "Asunto 4", "Asunto 5"].map((u, i) => (
            <div key={i} className="flex items-center justify-between text-[10px] py-1 border-b border-slate-100 last:border-b-0">
              <span className="text-slate-800">{u}</span>
              <Trash2 className="h-3 w-3 text-slate-300" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  TEMPLATES                                                                  */
/* -------------------------------------------------------------------------- */
function TemplatesSection() {
  return (
    <section id="tyopohjat" className="py-20 md:py-28">
      <div className={CONTAINER}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-slate-900">
              Luo työpohja kerran.<br />
              <span className="text-emerald-600">Käytä sitä kaikissa tulevissa töissä.</span>
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-xl">
              Kun yrityksesi yleisimmät työt on tallennettu työpohjiksi, uuden raportin luominen on helppoa. Valitse työn nimi, kohdetyyppi ja työpohja — Veraso muodostaa valmiin rungon automaattisesti.
            </p>
            <p className="mt-4 text-base text-slate-500 leading-relaxed">
              Päivitä työpohjaa milloin tahansa. Muutokset näkyvät uusissa töissä, mutta aiemmin tehdyt raportit säilyvät ennallaan.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-800" data-testid="templates-team-note">
              <UserPlus className="h-4 w-4" />
              Luo työpohja kerran — koko tiimi voi käyttää sitä kentällä.
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl bg-white border border-slate-200 p-6 md:p-8 shadow-[0_20px_60px_-25px_rgba(15,23,42,0.15)]">
              <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-600">Työpohjaan voit tallentaa</div>
              <ul className="mt-4 space-y-4">
                {[
                  { h: "Työvaiheet", body: "Vaiheet, jotka asentaja kuittaa työn aikana." },
                  { h: "Havaintolistat", body: "Tärkeät huomiot kirjataan yhdellä painalluksella." },
                  { h: "Pikatarvikkeet", body: "Käytetyt materiaalit eivät jää laskuttamatta." },
                  { h: "Yrityskohtaiset tiedot", body: "Juuri kyseisessä työssä tarvittavat kentät." },
                ].map((c, i) => (
                  <li key={i} className="flex items-start gap-3 pb-4 border-b border-slate-100 last:border-b-0 last:pb-0" data-testid={`template-item-${i}`}>
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 ring-1 ring-emerald-100">
                      <Check className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 text-base">{c.h}</div>
                      <div className="text-base text-slate-500 mt-0.5 leading-relaxed">{c.body}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  TEAM — Tiimi kentälle                                                     */
/* -------------------------------------------------------------------------- */
function TeamSection() {
  const steps = [
    {
      icon: Mail,
      n: "01",
      h: "Kutsu asentaja",
      body: "Lähetä kutsu sähköpostilla. Jokaisella käyttäjällä on oma kirjautuminen.",
    },
    {
      icon: Smartphone,
      n: "02",
      h: "Asentaja raportoi puhelimella",
      body: "Hän avaa Verason → valitsee työpohjan → aloittaa työn → lisää kuvat, työvaiheet, havainnot ja tarvikkeet.",
    },
    {
      icon: Inbox,
      n: "03",
      h: "Työ näkyy työnjohdolle",
      body: "Kentällä aloitetut työt löytyvät automaattisesti pääkäyttäjän Verasosta.",
    },
  ];
  return (
    <section id="tiimi" className="py-20 md:py-28 bg-slate-50/60" data-testid="team-section">
      <div className={CONTAINER}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: heading + steps */}
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Tiimi kentälle</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-slate-900">
              Anna asentajille<br />
              <span className="text-emerald-600">oma Veraso.</span>
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-xl max-w-xl">
              Kutsu asentajat mukaan. He raportoivat työn puhelimella, ja työnjohto näkee työt omassa Verasossaan.
            </p>

            <ol className="mt-10 space-y-5">
              {steps.map((s, i) => (
                <li key={i} className="flex items-start gap-5" data-testid={`team-step-${s.n}`}>
                  <div className="relative flex-shrink-0">
                    <div className="h-14 w-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-emerald-600 shadow-[0_10px_25px_-15px_rgba(15,23,42,0.2)]">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <div className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-emerald-500 text-white text-[11px] font-bold flex items-center justify-center tabular-nums ring-2 ring-slate-50/60">
                      {parseInt(s.n, 10)}
                    </div>
                  </div>
                  <div className="pt-1">
                    <div className="font-display font-semibold text-slate-900 text-lg">{s.h}</div>
                    <div className="text-base text-slate-600 mt-1 leading-relaxed">{s.body}</div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 ring-1 ring-emerald-100">
                <ArrowRight className="h-5 w-5" />
              </div>
              <div className="text-sm sm:text-base text-slate-700 leading-snug">
                <span className="font-semibold text-slate-900">Pääkäyttäjä rakentaa</span> → <span className="font-semibold text-slate-900">asentaja käyttää</span> → <span className="font-semibold text-slate-900">raportti palaa työnjohdolle.</span>
              </div>
            </div>
          </div>

          {/* Right: phone mockup — field worker inbox */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-[300px]">
              <div className="absolute -inset-8 -z-10 rounded-[48px] bg-gradient-to-br from-emerald-100/50 via-white to-transparent blur-2xl" />
              <div className="rounded-[36px] bg-slate-900 p-1.5 shadow-[0_35px_80px_-20px_rgba(15,23,42,0.4)]">
                <div className="rounded-[30px] bg-white overflow-hidden">
                  <div className="h-4 bg-slate-900 rounded-b-xl mx-auto w-20 relative -top-px" />
                  <div className="p-4 pt-3 pb-6 min-h-[500px]">
                    <PhoneFieldInbox />
                  </div>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs text-slate-500 font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Asentajan näkymä puhelimella
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneFieldInbox() {
  const jobs = [
    { title: "Sähkötarkastus, As Oy Rantatie 12", meta: "Aloitettu · 3 yksikköä", state: "in_progress" },
    { title: "IV-huolto, Kiint. Oy Metsäpolku", meta: "Tänään · 8 yksikköä", state: "new" },
    { title: "LVI-vuosihuolto, Rivitalo Kotila", meta: "Huomenna · 6 yksikköä", state: "new" },
  ];
  return (
    <div className="space-y-3">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600">Kenttä</div>
          <div className="font-display font-bold text-slate-900 text-base leading-tight">Omat työt</div>
        </div>
        <div className="h-8 w-8 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">M</div>
      </div>

      {/* Highlight card — invitation accepted */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-3.5">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest opacity-90">
          <Mail className="h-3 w-3" /> Tervetuloa tiimiin
        </div>
        <div className="mt-1.5 text-sm font-semibold leading-tight">Kutsun hyväksyntä onnistui</div>
        <div className="text-[11px] opacity-80 mt-0.5">Voit aloittaa raportoinnin heti.</div>
      </div>

      {/* Job list */}
      <div className="space-y-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Sinulle jaetut työt</div>
        {jobs.map((j, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-2.5 flex items-start gap-2.5">
            <div className={`h-8 w-8 rounded-lg flex-shrink-0 flex items-center justify-center ${j.state === "in_progress" ? "bg-amber-50 text-amber-600 ring-1 ring-amber-100" : "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"}`}>
              {j.state === "in_progress" ? <Loader2 className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-slate-900 leading-tight truncate">{j.title}</div>
              <div className="text-[9px] text-slate-500 mt-0.5">{j.meta}</div>
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300 mt-1.5" />
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <button className="w-full rounded-2xl bg-slate-900 text-white text-xs font-bold py-3 flex items-center justify-center gap-1.5 mt-1">
        <Plus className="h-3.5 w-3.5" /> Aloita uusi työ
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PRICING                                                                    */
/* -------------------------------------------------------------------------- */
function Pricing() {
  const features = [
    "Rajaton määrä raportteja",
    "Kaikki ominaisuudet käytössä",
    "Työpohjat ja asukastiedotteet",
  ];
  const tiers = [
    { name: "Solo", price: "39", body: "1 pääkäyttäjä. Rajaton käyttö ja kaikki ominaisuudet.", cta: "Aloita ilmaiseksi" },
    { name: "Tiimi", price: "89", body: "3 pääkäyttäjää. Yleisimmin valittu.", cta: "Aloita ilmaiseksi", highlight: true },
    { name: "Yritys", price: "149", body: "5 pääkäyttäjää. Kasvavalle tiimille.", cta: "Aloita ilmaiseksi" },
  ];
  return (
    <section id="hinnoittelu" className="py-20 md:py-28 bg-slate-50/60">
      <div className={CONTAINER}>
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Yksinkertainen hinnoittelu.</h2>
          <p className="mt-4 text-slate-600">Rajaton käyttö ja kaikki ominaisuudet jokaisessa paketissa. Peruuta milloin haluat.</p>
          <p className="mt-2 text-sm text-slate-500">Hinnat ilman arvonlisäveroa (ALV 0 %).</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <div key={i} className={`relative rounded-3xl p-8 border transition-transform hover:-translate-y-1 duration-300 ${t.highlight ? "bg-slate-950 text-white border-slate-950 shadow-2xl" : "bg-white border-slate-200"}`} data-testid={`pricing-${t.name.toLowerCase()}`}>
              {t.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest">Suositumpi</div>}
              <div className={`text-xs font-bold uppercase tracking-[0.2em] ${t.highlight ? "text-emerald-400" : "text-emerald-600"}`}>{t.name}</div>
              <div className="mt-4 flex items-baseline gap-1"><span className="font-display text-5xl font-bold">{t.price}</span><span className={`text-sm ${t.highlight ? "text-slate-400" : "text-slate-500"}`}>€ / kk</span></div>
              <p className={`mt-3 text-sm ${t.highlight ? "text-slate-300" : "text-slate-500"}`}>{t.body}</p>
              <ul className="mt-6 space-y-2.5">
                {features.map((f, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm">
                    <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${t.highlight ? "text-emerald-400" : "text-emerald-600"}`} />
                    <span className={t.highlight ? "text-slate-200" : "text-slate-700"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" onClick={() => track(`pricing_${t.name.toLowerCase()}`)} className={`mt-8 block text-center rounded-full font-semibold px-5 py-3 text-sm transition-colors ${t.highlight ? "bg-white hover:bg-slate-100 text-slate-900" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                {t.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  FAQ                                                                        */
/* -------------------------------------------------------------------------- */
function Faq() {
  const items = [
    { q: "Toimiiko Veraso puhelimella?", a: "Kyllä — puhelin on pääasiallinen työkalu kenttäraportointiin. Veraso on suunniteltu ensisijaisesti mobiilikäyttöön: koko raportointityö tehdään kentällä työn yhteydessä, ei toimistolla jälkikäteen. Voit luoda, muokata ja jakaa raportteja suoraan puhelimella. Tabletti ja tietokone toimivat samoin." },
    { q: "Voiko raportteihin lisätä kuvia?", a: "Kyllä. Asentaja ottaa kuvia työn aikana, ja ne tallentuvat suoraan sen yksikön tai työvaiheen alle, jossa työ tehdään. Kuvat säilyttävät ottoaikaleiman ja voidaan liittää havaintoihin." },
    { q: "Saako raportit PDF-muotoon?", a: "Kyllä. Kun työ on valmis, raportti muodostuu automaattisesti selkeäksi PDF:ksi yrityksesi omalla ilmeellä. Sen voi jakaa asiakkaalle linkkinä tai ladata tiedostona." },
    { q: "Voiko käyttää yrityksen omaa logoa?", a: "Kyllä. Lisää yrityksen logo asetuksiin, niin se ilmestyy automaattisesti jokaisen raportin ja asukastiedotteen otsikkoon." },
    { q: "Sopiiko Veraso huolto- ja palveluyrityksille?", a: "Erityisesti kyllä. Veraso on rakennettu pieniin ja keskisuuriin huolto-, LVI-, sähkö- ja kiinteistöpalvelu­yrityksille — 1–20 asentajan tiimeihin." },
    { q: "Voiko Veraso Reportsilla tehdä tarkastusraportteja?", a: "Kyllä. Yksiköt voivat olla huoneistoja, laitteita, tarkastuspisteitä tai työvaiheita. Sama työkalu skaalautuu yksittäisestä huoltokäynnistä monisivuiseen tarkastuskokonaisuuteen." },
    { q: "Voiko raportin jakaa kenttähenkilölle linkillä?", a: "Kyllä. Toimisto luo työn ja jakaa muokkauslinkin asentajalle. Ei kirjautumista, ei sovellusasennusta — asentaja avaa linkin puhelimen selaimessa ja aloittaa työn." },
  ];
  return (
    <section id="ukk" className="py-20 md:py-28">
      <div className={CONTAINER}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-slate-900">Usein kysytyt<br />kysymykset.</h2>
            <p className="mt-6 text-slate-600 leading-relaxed">Jos vastausta ei löydy alta, kirjoita meille alla olevalla lomakkeella — vastaamme yleensä saman päivän aikana.</p>
          </div>
          <div className="lg:col-span-8 space-y-3">
            {items.map((it, i) => <FaqItem key={i} q={it.q} a={it.a} testId={`faq-item-${i}`} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
function FaqItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
        <span className="font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className="grid transition-[grid-template-rows] duration-300" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
        <div className="overflow-hidden">
          <div className="px-5 pb-5 text-slate-600 leading-relaxed text-sm">{a}</div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CONTACT FORM                                                               */
/* -------------------------------------------------------------------------- */
function ContactForm() {
  const [state, setState] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!state.name.trim() || !state.email.trim() || !state.message.trim()) {
      toast.error("Täytä pakolliset kentät");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, state);
      setSent(true);
      toast.success("Viesti lähetetty. Palaamme asiaan pian.");
      track("contact_submit");
    } catch {
      toast.error("Lähetys ei onnistunut. Yritä uudelleen hetken kuluttua.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="ota-yhteytta" className="py-20 md:py-28 bg-slate-50/60">
      <div className={CONTAINER}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-slate-900">Ota yhteyttä.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Kysy hinnoittelusta, käyttöönotosta tai räätälöinnistä. Vastaamme yleensä saman päivän aikana.</p>
            <a
              href="mailto:info@veraso.fi"
              className="mt-5 inline-flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-4 py-3 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors group"
              data-testid="landing-contact-email"
            >
              <span className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center ring-1 ring-emerald-100 flex-shrink-0 group-hover:bg-emerald-100">
                <Mail className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold leading-tight">Sähköposti</span>
                <span className="block font-semibold text-slate-900 text-sm">info@veraso.fi</span>
              </span>
            </a>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-6 md:p-8 shadow-[0_20px_60px_-25px_rgba(15,23,42,0.15)]" data-testid="contact-form">
            {sent ? (
              <div className="text-center py-8">
                <div className="inline-flex h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 items-center justify-center mb-4"><Check className="h-7 w-7" /></div>
                <div className="font-display text-xl font-semibold text-slate-900">Kiitos yhteydenotostasi</div>
                <div className="text-sm text-slate-500 mt-2">Palaamme asiaan pian.</div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input label="Nimi *" value={state.name} onChange={(v) => setState({ ...state, name: v })} testId="contact-name" />
                  <Input label="Sähköposti *" value={state.email} onChange={(v) => setState({ ...state, email: v })} type="email" testId="contact-email" />
                </div>
                <Input label="Yritys" value={state.company} onChange={(v) => setState({ ...state, company: v })} testId="contact-company" />
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Viesti *</label>
                  <textarea value={state.message} onChange={(e) => setState({ ...state, message: e.target.value })} rows={5} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-slate-400 focus:ring-0 outline-none text-sm" placeholder="Mitä haluat kysyä?" data-testid="contact-message" />
                </div>
                <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 py-3 text-sm disabled:opacity-50" data-testid="contact-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {busy ? "Lähetetään…" : "Lähetä viesti"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
function Input({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-slate-400 focus:ring-0 outline-none text-sm" data-testid={testId} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  FOOTER                                                                     */
/* -------------------------------------------------------------------------- */
function Footer() {
  return (
    <footer className="border-t border-slate-200 py-12 md:py-16">
      <div className={CONTAINER}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="font-display font-semibold text-lg tracking-tight"><span className="text-blue-600">Veraso</span> <span className="text-emerald-600">Reports</span></span>
            </div>
            <p className="text-sm text-slate-500 max-w-xs leading-relaxed">Suomalaisten huoltoyritysten kenttäraportointi. Rakennettu pieniin ja keskisuuriin tiimeihin.</p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Tuote</div>
            <ul className="space-y-2 text-sm text-slate-700">
              <li><a href="#ratkaisu" className="hover:text-slate-900">Ratkaisu</a></li>
              <li><a href="#nain-toimii" className="hover:text-slate-900">Näin Veraso toimii</a></li>
              <li><a href="#hinnoittelu" className="hover:text-slate-900">Hinnoittelu</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Yritys</div>
            <ul className="space-y-2 text-sm text-slate-700">
              <li><Link to="/tietosuoja" className="hover:text-slate-900">Tietosuoja</Link></li>
              <li><Link to="/ehdot" className="hover:text-slate-900">Käyttöehdot</Link></li>
              <li><Link to="/login" className="hover:text-slate-900">Kirjaudu</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Veraso Oy · Kaikki oikeudet pidätetään.</span>
          <span>Tehty Suomessa</span>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/*  FLOATING BOTTOM CTA — visible after hero is scrolled past                  */
/* -------------------------------------------------------------------------- */
function FloatingCta() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById("top");
      if (!hero) { setVisible(window.scrollY > 700); return; }
      const rect = hero.getBoundingClientRect();
      // Show once the hero section is fully scrolled past
      setVisible(rect.bottom < 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      aria-hidden={!visible}
      className={`fixed bottom-4 inset-x-0 z-40 flex justify-center px-4 pointer-events-none transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
      data-testid="floating-cta-wrap"
    >
      <div className="pointer-events-auto inline-flex items-center gap-3 rounded-full bg-white/95 backdrop-blur border border-slate-200 pl-4 pr-1.5 py-1.5 shadow-2xl shadow-slate-900/10">
        <span className="hidden sm:inline text-sm text-slate-600">
          <span className="font-semibold text-slate-800">30 päivän kokeilu</span>
          <span className="text-slate-300 mx-1.5">·</span>
          Ei luottokorttia
        </span>
        <Link
          to="/register"
          onClick={() => track("floating_register")}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 text-sm transition-all hover:-translate-y-0.5"
          data-testid="floating-cta"
        >
          Aloita ilmaiseksi <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
